import { BADGE_FALLBACK } from '@uefadigital/panenka';
import { PkBadge, PkIdentifier } from '@uefadigital/panenka-react';
import { memo, useMemo } from 'react';

import { useLivescoresOptions } from '../../store';
import type { LivescoreCompetition } from '../../store/types';
import { getCompCode } from '../../utils/compCode';
import { getCompName } from '../../utils/compName';
import { track } from '../../utils/tracking';

import { useCountry } from '@hooks/useCountry';
import Image from '@shared/Image';
import PanenkaDropdown from '@shared/PanenkaDropdown';
import { useTranslateFn } from '@shared/Translate';
import type { D3Country } from '@utilities/country';
import { imgBaseUrl } from '@utilities/WindowContext';

import * as classes from './styles.module.scss';

export type FilterState = {
  country: D3Country;
  competition: LivescoreCompetition;
};

const CompetitionIdentifier = ({
  competition,
  selected,
  onIdentifierSelected,
}: {
  competition: LivescoreCompetition;
  selected: boolean;
  onIdentifierSelected: (event: CustomEvent<{ selected: boolean }>) => void;
}) => {
  const compCode = getCompCode(competition);
  const compName = getCompName(competition);
  const logo = `${imgBaseUrl}/imgml/uefacom/elements/logos/competitions/light/${compCode}` + '.svg';

  return (
    <PkIdentifier
      alignment='center'
      type='vertical'
      className={classes.identifier}
      enableSelection
      selected={selected}
      onPkChange={onIdentifierSelected}
    >
      <Image slot='prefix' src={logo} width={43} height={43} alt={compName} />
      <span className='pk-text--text-02 pk-text--wrap pk-font--base-regular' slot='primary'>
        {compName}
      </span>
    </PkIdentifier>
  );
};

const CountryButtonContent = memo(({ element: country }: { element: D3Country }) => {
  if (!country) return null;

  return (
    <div className='pk-d--flex pk-align-items--center pk-gap--xs'>
      <PkBadge
        size={24}
        fallbackImage={BADGE_FALLBACK.CLUB_GENERIC_BADGE}
        src={`${imgBaseUrl}/imgml/flags/70x70/${country.fifaCountryCode}.png`}
        alt={country.countryName}
      />
      <span className='pk-text--text-01 pk-font--base-regular pk-font-size--m'>{country.countryName}</span>
    </div>
  );
});
CountryButtonContent.displayName = 'CountryButtonContent';

const CountryItemContent = memo(({ element: c }: { element: D3Country }) => (
  <span className='pk-text--text-01 pk-font--base-regular pk-font-size--m'>{c.countryName}</span>
));
CountryItemContent.displayName = 'CountryItemContent';

const ModalContent = ({
  localState,
  onContentUpdate,
}: {
  initialState: FilterState;
  localState: FilterState;
  onContentUpdate: (newState: FilterState | ((current: FilterState) => FilterState)) => void;
}) => {
  const t = useTranslateFn();
  const { options } = useLivescoresOptions();
  const userCountry = useCountry();

  const handleCompetitionSelect = (competition: LivescoreCompetition) => (e: CustomEvent<{ selected: boolean }>) => {
    track('Select competition', 'uefa_calendar_select_competition');

    const comp = { ...competition, code: getCompCode(competition) };

    onContentUpdate((state) => ({
      ...state,
      competition: e.detail.selected ? comp : null,
    }));
  };

  const handleCountrySelect = (e: D3Country) => {
    track('Select country', 'uefa_calendar_select_country');

    onContentUpdate((state) => ({
      ...state,
      country: e,
    }));
  };

  const countries = useMemo(
    () =>
      options.countries
        ?.filter((c) => !!c.fifaCountryCode)
        .map((c) => ({ ...c, countryName: t(`country_${c.fifaCountryCode.toLowerCase()}`) }))
        .toSorted((country) =>
          userCountry?.countryCode === country.countryCode && userCountry?.regionCodeShort === country.regionCodeShort
            ? -1
            : 1,
        ),
    [options.countries, t, userCountry?.countryCode, userCountry?.regionCodeShort],
  );

  const selectedCountry = useMemo(
    () =>
      countries?.find(
        (c) =>
          c.countryCode === localState.country?.countryCode &&
          c.regionCodeShort === localState.country?.regionCodeShort,
      ),
    [countries, localState.country],
  );

  if (!options.competitions?.length && !countries?.length) return null;

  return (
    <>
      <div className='pk-font--base-semibold pk-text--text-01 pk-line-height--md pk-font-size--l pk-mb--m'>
        {t('broadcaster_country')}
      </div>
      <PanenkaDropdown
        elements={countries}
        selectedItem={selectedCountry}
        onSelect={handleCountrySelect}
        ButtonContent={CountryButtonContent}
        MenuItemContent={CountryItemContent}
        buttonTheme='light'
        dropdownClassName='pk-align-self--start pk-border pk-rounded--l'
        iconColor='ui-04'
        highContrastButton={false}
        circleIcon={false}
      />
      <div className='pk-font--base-semibold pk-text--text-01 pk-line-height--md pk-font-size--l pk-mb--m pk-mt--xl'>
        {t('label_filter_by_competition')}
      </div>
      <div className={classes.grid}>
        {options.competitions.map((competition) => (
          <CompetitionIdentifier
            key={`competitions-${competition.id}-${competition.phase}`}
            competition={competition}
            selected={
              localState?.competition?.id === competition.id &&
              (!localState?.competition?.phase || localState.competition.phase === competition.phase)
            }
            onIdentifierSelected={handleCompetitionSelect(competition)}
          />
        ))}
      </div>
    </>
  );
};

export default memo(ModalContent);
